import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, MapPin, Plane, Users, Calendar, Search, MapPinned, Bus} from 'lucide-react';
import CalendarComponent from './CalendarComponent';

function SearchComponent({ onSearch }) {
  const navigate = useNavigate();
  const [paquetes, setPaquetes] = useState([]);
  const [puntosAscenso, setPuntosAscenso] = useState([]);
  const [searchValues, setSearchValues] = useState({
    pasajeros: 2,
    destino: '',
    transporte: '',
    puntoAscenso: '',
    fecha: ''
  });
  const [dropdowns, setDropdowns] = useState({
    pasajeros: false,
    destino: false,
    transporte: false,
    puntoAscenso: false,
    fecha: false
  });
  const [filteredDestinos, setFilteredDestinos] = useState([]);
  const [transportesDisponibles, setTransportesDisponibles] = useState([]);
  const [puntosAscensoDisponibles, setPuntosAscensoDisponibles] = useState([]);
  const [fechasDisponibles, setFechasDisponibles] = useState([]);
  const [loading, setLoading] = useState(false);

  const pasajerosRef = useRef();
  const destinoRef = useRef();
  const transporteRef = useRef();
  const puntoAscensoRef = useRef();
  const fechaRef = useRef();

  // Cargar paquetes y puntos de ascenso
useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [paquetesRes, puntosRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/paquetes`),
        fetch(`${process.env.REACT_APP_API_URL}/puntos-ascenso-local`)
      ]);

      if (paquetesRes.ok) {
        const paquetesData = await paquetesRes.json();
        setPaquetes(paquetesData);
        console.log('📦 Paquetes:', paquetesData.length);
      }

      if (puntosRes.ok) {
        const puntosData = await puntosRes.json();
        setPuntosAscenso(puntosData.puntos_de_ascenso || []);
        console.log('📍 Puntos:', puntosData.puntos_de_ascenso?.length);
      }
      
    } catch (error) {
      console.error('❌ Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  fetchData();
}, []);

  // Filtrar por número de pasajeros (singles)
  const paquetesFiltradosPorPasajeros = searchValues.pasajeros === 1
    ? paquetes.filter(p => {
        const singlesValue = (p.singles || '').toLowerCase().trim();
        return singlesValue === 'si' || singlesValue === 'sí';
      })
    : paquetes; // Para 2+ pasajeros, mostrar todos

      // Filtrar destinos (depende de pasajeros)
      useEffect(() => {
      if (paquetesFiltradosPorPasajeros.length > 0) {
        const destinosMap = new Map();
        
        paquetesFiltradosPorPasajeros
          .filter(p => {
            // ✅ USAR SOLO destino_nombre (no título)
            const nombre = p.destino_nombre?.trim();
            
            // Ignorar paquetes sin destino_nombre válido
            if (!nombre) return false;
            
            // Para filtrar dropdown: búsqueda parcial mientras escribe
            const cumpleDestino = searchValues.destino 
              ? nombre.toLowerCase().includes(searchValues.destino.toLowerCase())
              : true;
            
            const cumpleDisponibilidad = p.disponibilidad >= searchValues.pasajeros;
            
            return cumpleDestino && cumpleDisponibilidad;
      })
      .forEach(p => {
        const nombre = p.destino_nombre?.trim();
        if (nombre) {
          if (!destinosMap.has(nombre)) {
            destinosMap.set(nombre, { nombre, paquetes: 0 });
          }
          destinosMap.get(nombre).paquetes++;
        }
      });
    
    const destinosOrdenados = Array.from(destinosMap.values()).sort((a, b) => 
      a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
    );
    
    setFilteredDestinos(destinosOrdenados);
  }
}, [searchValues.pasajeros, searchValues.destino, paquetes]);

  // Filtrar transportes (depende de destino y pasajeros)
  useEffect(() => {
  if (searchValues.destino) {
    // ✅ COMPARACIÓN EXACTA usando destino_nombre
    const paquetesDestino = paquetesFiltradosPorPasajeros.filter(p => {
    const nombre = p.destino_nombre?.trim();
      
      // Comparación exacta (case-insensitive)
      return nombre?.toLowerCase() === searchValues.destino.toLowerCase() &&
            p.disponibilidad >= searchValues.pasajeros;
    });

    console.log('🚌 Paquetes encontrados para transportes:', paquetesDestino.length);
    console.log('   Títulos:', paquetesDestino.map(p => p.titulo));
    
    const transportes = [...new Set(paquetesDestino.map(p => p.transporte).filter(Boolean))];
    setTransportesDisponibles(transportes);
  } else {
    setTransportesDisponibles([]);
    setSearchValues(prev => ({ ...prev, transporte: '', puntoAscenso: '', fecha: '' }));
  }
}, [searchValues.destino, searchValues.pasajeros, paquetes]);

// ✅ VERSIÓN LIMPIA - Reemplazar en SearchComponent.jsx

// 1️⃣ Filtrar puntos de ascenso (depende de destino, transporte y pasajeros)
useEffect(() => {
  if (!searchValues.destino || !searchValues.transporte) {
    console.log('🧹 Limpiando puntos de ascenso');
    setPuntosAscensoDisponibles([]);
    setSearchValues(prev => ({ ...prev, puntoAscenso: '' }));
    return;
  }
  
  console.log('🔍 Filtrando puntos de ascenso...');
  console.log('   Destino:', searchValues.destino);
  console.log('   Transporte:', searchValues.transporte);
  
  // ✅ Si es AVIÓN, poner "Buenos Aires" automático
  if (searchValues.transporte === 'Aéreo' || searchValues.transporte === 'Aereo' || searchValues.transporte === 'Avión') {
    console.log('✈️ Transporte aéreo - Buenos Aires automático');
    const buenosAires = { id: 'AEROBUS', nombre: 'Buenos Aires' };
    setPuntosAscensoDisponibles([buenosAires]);
    setSearchValues(prev => ({ ...prev, puntoAscenso: '' }));
    return;
  }
  
  // ✅ Para bus/semi-cama: Filtrar paquetes que cumplan destino + transporte
  const paquetesDestino = paquetesFiltradosPorPasajeros.filter(p => {
    const nombre = p.destino_nombre?.trim();
    return nombre?.toLowerCase() === searchValues.destino.toLowerCase() &&
           p.transporte === searchValues.transporte &&
           p.disponibilidad >= searchValues.pasajeros;
  });

  console.log('📦 Paquetes encontrados:', paquetesDestino.length);

  // Extraer IDs únicos de puntos_ascenso_ids (que es un array en PostgreSQL)
  const puntosIdsSet = new Set();
  paquetesDestino.forEach(p => {
    if (p.puntos_ascenso_ids && Array.isArray(p.puntos_ascenso_ids)) {
      p.puntos_ascenso_ids.forEach(id => puntosIdsSet.add(parseInt(id)));
    }
  });

  console.log('📍 IDs de puntos únicos:', Array.from(puntosIdsSet));

  // Filtrar puntos que existen en la lista completa
  const puntosDisponibles = puntosAscenso.filter(punto => 
    puntosIdsSet.has(parseInt(punto.id))
  );

  console.log(`✅ ${puntosDisponibles.length} puntos disponibles para este destino/transporte`);
  console.log('   Puntos:', puntosDisponibles.map(p => `[${p.id}] ${p.nombre}`));

  setPuntosAscensoDisponibles(puntosDisponibles);

  if (puntosDisponibles.length === 0) {
    console.log('⚠️ No hay puntos disponibles para estos filtros');
    setSearchValues(prev => ({ ...prev, puntoAscenso: '' }));
  }
}, [searchValues.destino, searchValues.transporte, searchValues.pasajeros, paquetes, puntosAscenso, paquetesFiltradosPorPasajeros]);


// ✅ AGREGAR este useEffect para manejar el cambio de transporte
useEffect(() => {
  // Cuando cambia el transporte, limpiar punto de ascenso
  if (searchValues.transporte) {
    console.log('🔄 Transporte cambió, limpiando punto de ascenso');
    setSearchValues(prev => ({ ...prev, puntoAscenso: '', fecha: '' }));
  }
}, [searchValues.transporte]);

// 2️⃣ Filtrar fechas (depende de destino, transporte, punto de ascenso y pasajeros)
useEffect(() => {
  if (searchValues.destino && searchValues.transporte) {
    // ✅ COMPARACIÓN EXACTA usando destino_nombre
    const paquetesFiltrados = paquetesFiltradosPorPasajeros.filter(p => {
      const nombre = p.destino_nombre?.trim();
      
      // Comparación exacta del destino
      const cumpleDestino = nombre?.toLowerCase() === searchValues.destino.toLowerCase();
      const cumpleTransporte = p.transporte === searchValues.transporte;
      const cumplePunto = !searchValues.puntoAscenso || 
        (p.puntos_ascenso_ids && p.puntos_ascenso_ids.includes(parseInt(searchValues.puntoAscenso)));
      
      return cumpleDestino && cumpleTransporte && cumplePunto && p.disponibilidad >= searchValues.pasajeros;
    });

    console.log('📅 Paquetes encontrados para fechas:', paquetesFiltrados.length);
    console.log('   Títulos:', paquetesFiltrados.map(p => p.titulo));
    
    const fechas = paquetesFiltrados
      .filter(p => p.fecha_salida)
      .map(p => ({
        fecha: p.fecha_salida,
        destacado: p.destacado || false,
        precio: p.tarifa,
        titulo: p.titulo,
        id: p.id,
        disponibilidad: p.disponibilidad,
        impuestos: p.impuestos,
        descuento_principal_monto: p.descuento_principal_monto,
        descuento_principal_tipo: p.descuento_principal_tipo,
        descuento_principal_desde: p.descuento_principal_desde,
        descuento_principal_hasta: p.descuento_principal_hasta,
        tarifa: p.tarifa,
        paquete: p
      }));
    
    console.log('📅 Fechas cargadas:', fechas);
    
    setFechasDisponibles(fechas);
  } else {
    setFechasDisponibles([]);
  }
}, [searchValues.destino, searchValues.transporte, searchValues.puntoAscenso, searchValues.pasajeros, paquetes]);



  const toggleDropdown = (field) => {
    setDropdowns(prev => ({
      ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      [field]: !prev[field]
    }));
  };

  const selectDestino = (destino) => {
    setSearchValues(prev => ({ ...prev, destino: destino.nombre }));
    setDropdowns(prev => ({ ...prev, destino: false }));
  };

  const selectTransporte = (transporte) => {
    setSearchValues(prev => ({ ...prev, transporte }));
    setDropdowns(prev => ({ ...prev, transporte: false }));
  };

  const selectPuntoAscenso = (punto) => {
  // ✅ Si es aéreo, NO asignar valor real (mantener vacío)
  if (searchValues.transporte === 'Aéreo' || searchValues.transporte === 'Aereo' || searchValues.transporte === 'Avión') {
    console.log('✈️ Transporte aéreo - NO asignando puntoAscenso al estado');
    setSearchValues(prev => ({ ...prev, puntoAscenso: '' })); // Mantener vacío
  } else {
    // Para bus/colectivo, asignar normalmente
    setSearchValues(prev => ({ ...prev, puntoAscenso: punto.id }));
  }
  setDropdowns(prev => ({ ...prev, puntoAscenso: false }));
};

const handleBuscar = () => {
  const params = new URLSearchParams();
  if (searchValues.destino) params.set('destino', searchValues.destino);
  if (searchValues.transporte) params.set('transporte', searchValues.transporte);
  if (searchValues.puntoAscenso) params.set('puntoAscenso', searchValues.puntoAscenso);
  if (searchValues.pasajeros) params.set('pasajeros', searchValues.pasajeros);
  
  // ✅ Asegurar que la fecha esté en formato YYYY-MM-DD
  let fechaBusqueda = searchValues.fecha;
  if (fechaBusqueda instanceof Date) {
    fechaBusqueda = fechaBusqueda.toISOString().split('T')[0];
  }
  
  if (fechaBusqueda) {
    params.set('fecha', fechaBusqueda);
  }
  
  console.log('🔍 DEBUG Búsqueda:');
  console.log('   Pasajeros seleccionados:', searchValues.pasajeros);
  console.log('   Fecha seleccionada:', fechaBusqueda);
  console.log('   Total fechas disponibles:', fechasDisponibles.length);
  
  // ✅ SOLO agregar paqueteId si hay UN SOLO paquete que cumple las condiciones
  if (fechaBusqueda) {
    // Filtrar paquetes que cumplen con fecha y pasajeros
    const paquetesCandidatos = fechasDisponibles.filter(f => {
      const fechaPaquete = f.fecha.split('T')[0];
      
      // ✅ PASO 1: Debe coincidir la fecha
      if (fechaPaquete !== fechaBusqueda) return false;
      
      // ✅ PASO 2: Validar disponibilidad y tipo de habitación
      const paquete = f.paquete || f;
      const cumpleDisponibilidad = paquete.disponibilidad >= searchValues.pasajeros;
      
      // ✅ Si es 1 pasajero, debe ser single
      if (searchValues.pasajeros === 1) {
        const singlesValue = (paquete.singles || '').toLowerCase().trim();
        const esSingle = singlesValue === 'si' || singlesValue === 'sí';
        
        const cumple = esSingle && cumpleDisponibilidad;
        console.log(`   [${paquete.id}] ${paquete.titulo}: singles=${paquete.singles}, disponibilidad=${paquete.disponibilidad}, cumple=${cumple}`);
        
        return cumple;
      }
      
      // ✅ Si es 2+ pasajeros, NO debe ser single (habitación doble/triple)
      const singlesValue = (paquete.singles || '').toLowerCase().trim();
      const esSingle = singlesValue === 'si' || singlesValue === 'sí';
      
      const cumple = !esSingle && cumpleDisponibilidad;
      console.log(`   [${paquete.id}] ${paquete.titulo}: singles=${paquete.singles}, disponibilidad=${paquete.disponibilidad}, cumple=${cumple}`);
      
      return cumple;
    });
    
    console.log(`📊 Resultado: ${paquetesCandidatos.length} paquete(s) que cumplen condiciones`);
    
    // ✅ SOLO agregar paqueteId si hay EXACTAMENTE 1 paquete
    if (paquetesCandidatos.length === 1) {
      const paqueteUnico = paquetesCandidatos[0];
      params.set('paqueteId', paqueteUnico.id);
      console.log('✅ UN SOLO paquete encontrado, agregando paqueteId:', paqueteUnico.id);
      console.log('   Título:', paqueteUnico.titulo || paqueteUnico.paquete?.titulo);
    } else if (paquetesCandidatos.length > 1) {
      console.log('📋 MÚLTIPLES paquetes encontrados, NO agregando paqueteId');
      console.log('   Paquetes:', paquetesCandidatos.map(p => ({
        id: p.id,
        titulo: p.titulo || p.paquete?.titulo,
        singles: p.paquete?.singles || p.singles
      })));
      // NO agregar paqueteId, dejar que los filtros muestren todos
    } else {
      console.warn('⚠️ No se encontraron paquetes que cumplan las condiciones');
    }
  }
  
  const url = `/paquetes?${params.toString()}`;
  console.log('🌐 Navegando a:', url);
  
  navigate(url);
};

  useEffect(() => {
    const handleClickOutside = (event) => {
      const refs = [pasajerosRef, destinoRef, transporteRef, puntoAscensoRef, fechaRef];
      const isClickOutside = refs.every(ref => ref.current && !ref.current.contains(event.target));
      if (isClickOutside) {
        setDropdowns({
          pasajeros: false,
          destino: false,
          transporte: false,
          puntoAscenso: false,
          fecha: false
        });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-3xl shadow-xl">
      <div className="container mx-auto px-2 md:px-4 py-3 md:py-6">
        {loading && (
          <div className="text-center py-4">
            <div className="inline-flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#2AA8B0] mr-2"></div>
              <span className="text-gray-600">Cargando destinos...</span>
            </div>
          </div>
        )}
        
        <div className="flex flex-wrap lg:flex-nowrap gap-1.5 md:gap-3 items-center justify-center">
          {/* 1. PASAJEROS */}
          <div className="relative min-w-[140px]" ref={pasajerosRef}>
            <button
              onClick={() => toggleDropdown('pasajeros')}
              className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-2xl border-2 border-gray-200 hover:border-[#2AA8B0] focus:outline-none focus:border-[#2AA8B0] shadow-sm transition-all duration-300"
            >
              <div className="flex items-center">
                <Users size={18} className="text-[#2AA8B0] mr-2" />
                <span className="font-medium text-black">
                  {searchValues.pasajeros} {searchValues.pasajeros === 1 ? 'Pasajero' : 'Pasajeros'}
                </span>
              </div>
              <ChevronDown size={18} className={`text-gray-400 transform transition-transform duration-200 ${dropdowns.pasajeros ? 'rotate-180' : ''}`} />
            </button>
            {dropdowns.pasajeros && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 mt-2 overflow-hidden">
                {[1, 2, 3, 4, 5].map(num => (
                  <button
                    key={num}
                    onClick={() => {
                      setSearchValues(prev => ({ ...prev, pasajeros: num, destino: '', transporte: '', puntoAscenso: '', fecha: '' }));
                      setDropdowns(prev => ({ ...prev, pasajeros: false }));
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-b-0 flex items-center justify-between"
                  >
                    <span className="text-gray-900">{num} {num === 1 ? 'Pasajero' : 'Pasajeros'}</span>
                    {num === 1 && <span className="text-xs text-gray-500">(Solo singles)</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 2. DESTINO */}
          <div className="relative flex-1 min-w-[200px]" ref={destinoRef}>
            <button
              onClick={() => toggleDropdown('destino')}
              disabled={!searchValues.pasajeros}
              className={`w-full flex items-center justify-between px-4 py-3 bg-white rounded-2xl border-2 shadow-sm transition-all duration-300 ${
                searchValues.pasajeros
                  ? 'border-gray-200 hover:border-[#2AA8B0] focus:outline-none focus:border-[#2AA8B0]'
                  : 'border-gray-100 opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center">
                <MapPin size={18} className={`mr-2 ${searchValues.pasajeros ? 'text-[#2AA8B0]' : 'text-gray-300'}`} />
                <span className={`font-medium ${searchValues.pasajeros ? 'text-gray-700' : 'text-gray-400'}`}>
                  {searchValues.destino || 'Destino'}
                </span>
              </div>
              <ChevronDown size={18} className={`text-gray-400 transform transition-transform duration-200 ${dropdowns.destino ? 'rotate-180' : ''}`} />
            </button>
            {dropdowns.destino && searchValues.pasajeros && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 mt-2 overflow-hidden">
                <input
                  type="text"
                  placeholder="Buscar destino..."
                  className="w-full px-4 py-3 border-b border-gray-200 focus:outline-none focus:border-[#2AA8B0] text-black"
                  value={searchValues.destino}
                  onChange={(e) => setSearchValues(prev => ({ ...prev, destino: e.target.value }))}
                  autoFocus
                />
                <div className="max-h-64 overflow-y-auto">
                  {filteredDestinos.length > 0 ? (
                    filteredDestinos.map((destino, index) => {
                      const paquetesDestino = paquetes.filter(p => {
                        const nombre = p.destino_nombre?.trim();
                        return nombre === destino.nombre;
                      });
                      const maxDisponibilidad = Math.max(...paquetesDestino.map(p => p.disponibilidad || 0));
                      
                      return (
                        <button
                          key={index}
                          onClick={() => selectDestino(destino)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors flex items-center justify-between border-b border-gray-50 last:border-b-0"
                        >
                          <div className="flex items-center">
                            <MapPin size={16} className="text-[#2AA8B0] mr-3" />
                            <span className="font-medium text-gray-700">{destino.nombre}</span>
                          </div>
                          {/* 
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                              {destino.paquetes} paquetes
                            </span>
                            {searchValues.pasajeros > maxDisponibilidad && (
                              <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                                Máx. {maxDisponibilidad}
                              </span>
                            )}
                          </div>
                          */}
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-center">
                      No hay destinos disponibles para {searchValues.pasajeros} {searchValues.pasajeros === 1 ? 'pasajero' : 'pasajeros'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 3. TRANSPORTE */}
          <div className="relative flex-1 min-w-[160px]" ref={transporteRef}>
            <button
              onClick={() => toggleDropdown('transporte')}
              disabled={!searchValues.destino}
              className={`w-full flex items-center justify-between px-4 py-3 bg-white rounded-2xl border-2 shadow-sm transition-all duration-300 ${
                searchValues.destino
                  ? 'border-gray-200 hover:border-[#2AA8B0] focus:outline-none focus:border-[#2AA8B0]'
                  : 'border-gray-100 opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center">
                {searchValues.transporte === 'Aéreo' || searchValues.transporte === 'Avión' ? (
                  <Plane size={18} className={`mr-2 ${searchValues.destino ? 'text-[#2AA8B0]' : 'text-gray-300'}`} />
                ) : searchValues.transporte === 'Semi-cama' || searchValues.transporte === 'Cama' ? (
                  <Bus size={18} className={`mr-2 ${searchValues.destino ? 'text-[#2AA8B0]' : 'text-gray-300'}`} />
                ) : (
                  <Plane size={18} className={`mr-2 ${searchValues.destino ? 'text-[#2AA8B0]' : 'text-gray-300'}`} />
                )}
                <span className={`font-medium ${searchValues.destino ? 'text-gray-700' : 'text-gray-400'}`}>
                  {searchValues.transporte || 'Transporte'}
                </span>
              </div>
              <ChevronDown size={18} className={`text-gray-400 transform transition-transform duration-200 ${dropdowns.transporte ? 'rotate-180' : ''}`} />
            </button>
            {dropdowns.transporte && searchValues.destino && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 mt-2 overflow-hidden">
                <div className="max-h-48 overflow-y-auto">
                  {transportesDisponibles.length > 0 ? (
                    transportesDisponibles.map((transporte, index) => (
                      <button
                        key={index}
                        onClick={() => selectTransporte(transporte)}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors flex items-center border-b border-gray-50 last:border-b-0"
                      >
                        {transporte === 'Aéreo' || transporte === 'Avión' ? (
                          <Plane size={16} className="text-[#2AA8B0] mr-3" />
                        ) : (
                          <Bus size={16} className="text-[#2AA8B0] mr-3" />
                        )}
                        <span className="font-medium text-gray-700">{transporte}</span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-center text-sm">
                      No hay transportes disponibles
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 4. PUNTO DE ASCENSO */}
          <div className="relative flex-1 min-w-[180px]" ref={puntoAscensoRef}>
            <button
              onClick={() => toggleDropdown('puntoAscenso')}
              disabled={!searchValues.transporte}
              className={`w-full flex items-center justify-between px-4 py-3 bg-white rounded-2xl border-2 shadow-sm transition-all duration-300 ${
                searchValues.transporte
                  ? 'border-gray-200 hover:border-[#2AA8B0] focus:outline-none focus:border-[#2AA8B0]'
                  : 'border-gray-100 opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center">
                <MapPinned size={18} className={`mr-2 ${searchValues.transporte ? 'text-[#2AA8B0]' : 'text-gray-300'}`} />
                <span className={`font-medium text-sm ${searchValues.transporte ? 'text-gray-700' : 'text-gray-400'}`}>
                  {(() => {
                    // ✅ Si es aéreo, mostrar "Buenos Aires" de forma informativa (sin asignar al estado)
                    if (searchValues.transporte === 'Aéreo' || searchValues.transporte === 'Aereo' || searchValues.transporte === 'Avión') {
                      return 'Buenos Aires';
                    }
                    // Si es otro transporte, mostrar el punto seleccionado
                    if (searchValues.puntoAscenso) {
                      return puntosAscenso.find(p => parseInt(p.id) === parseInt(searchValues.puntoAscenso))?.nombre || 'Punto Ascenso';
                    }
                    return 'Punto Ascenso';
                  })()}
                </span>
              </div>
              <ChevronDown size={18} className={`text-gray-400 transform transition-transform duration-200 ${dropdowns.puntoAscenso ? 'rotate-180' : ''}`} />
            </button>
            
            {/* ✅ Solo mostrar dropdown si NO es aéreo y hay puntos disponibles */}
            {dropdowns.puntoAscenso && searchValues.transporte && 
            !(searchValues.transporte === 'Aéreo' || searchValues.transporte === 'Aereo' || searchValues.transporte === 'Avión') && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 mt-2 overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  {puntosAscensoDisponibles.length > 0 ? (
                    puntosAscensoDisponibles.map((punto) => (
                      <button
                        key={punto.id}
                        onClick={() => selectPuntoAscenso(punto)}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors flex items-start border-b border-gray-50 last:border-b-0"
                      >
                        <MapPinned size={16} className="text-[#2AA8B0] mr-3 flex-shrink-0 mt-0.5" />
                        <span className="font-medium text-gray-700 text-sm">{punto.nombre}</span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-center text-sm">
                      No hay puntos de ascenso disponibles
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 5. FECHA */}
          <div className="relative flex-1 min-w-[160px]" ref={fechaRef}>
            <button
              onClick={() => toggleDropdown('fecha')}
              disabled={!searchValues.transporte}
              className={`w-full flex items-center justify-between px-4 py-3 bg-white rounded-2xl border-2 shadow-sm transition-all duration-300 ${
                searchValues.transporte
                  ? 'border-gray-200 hover:border-[#2AA8B0] focus:outline-none focus:border-[#2AA8B0]'
                  : 'border-gray-100 opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center">
                <Calendar size={18} className={`mr-2 ${searchValues.transporte ? 'text-[#2AA8B0]' : 'text-gray-300'}`} />
                <span className={`font-medium ${searchValues.transporte ? 'text-gray-700' : 'text-gray-400'}`}>
                  {searchValues.fecha ? new Date(searchValues.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : 'Fecha'}
                </span>
              </div>
              <ChevronDown size={18} className={`text-gray-400 transform transition-transform duration-200 ${dropdowns.fecha ? 'rotate-180' : ''}`} />
            </button>
            {dropdowns.fecha && searchValues.transporte && (
              <div className="absolute top-full left-0 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 mt-2 overflow-hidden">
                <CalendarComponent
                  fechasDisponibles={fechasDisponibles}
                  onDateSelect={(fecha) => {
                    // ✅ CONVERTIR a formato YYYY-MM-DD
                    const fechaString = typeof fecha === 'string' 
                      ? fecha 
                      : fecha.toISOString().split('T')[0];
                    setSearchValues(prev => ({ ...prev, fecha }));
                    setDropdowns(prev => ({ ...prev, fecha: false }));

                    console.log('📅 Fecha seleccionada:', fechaString);
                  }}
                  selectedDate={searchValues.fecha}
                  destino={searchValues.destino || 'este destino'}
                />
              </div>
            )}
          </div>

          {/* BOTÓN BUSCAR */}
          <button
            onClick={handleBuscar}
            disabled={!searchValues.destino}
            className="bg-[#2AA8B0] text-white px-6 py-3 rounded-2xl hover:bg-[#1E3A5F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
          >
            <Search size={20} />
            <span className="font-semibold">Buscar</span>
          </button>
        </div>

        {/* Mensaje disponibilidad */}
        {searchValues.pasajeros > 1 && filteredDestinos.length === 0 && searchValues.destino && (
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-xl">
            <div className="flex items-start gap-2">
              <div className="text-orange-500 mt-0.5">⚠️</div>
              <div className="text-sm text-orange-700">
                <p className="font-medium">No hay paquetes disponibles para {searchValues.pasajeros} pasajeros</p>
                <p className="text-xs mt-1">Reduce el número de pasajeros o busca otro destino</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchComponent;